import React from 'react'
import { useIntl } from 'react-intl'

import { Download, Lock } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button'
import { NavGroup, NavGroupItem } from '@/components/atoms/NavGroup/NavGroup.tsx'
import { SearchBox } from '@/components/atoms/SearchBox/SearchBox'
import { AdminHeader, type AdminHeaderProps } from '@/components/organisms/AdminHeader/AdminHeader.tsx'
import {
  PromotionPlacementsForm,
  type PromotionPlacementsFormProps,
} from '@/components/organisms/PromotionPlacementsForm/PromotionPlacementsForm.tsx'

export interface AdminPromotionPlacementsViewProps
  extends Pick<AdminHeaderProps, 'onBackToPiloteClicked'>, PromotionPlacementsFormProps {
  readonly onExport?: () => void | Promise<void>
  readonly isExportPending?: boolean
}

export const AdminPromotionPlacementsView: React.FC<AdminPromotionPlacementsViewProps> = ({
  onBackToPiloteClicked,
  regions = [],
  onExport,
  isExportPending,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const intl = useIntl()

  const filteredRegions = regions.filter((region) => region.name.toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <div className="flex h-full flex-col">
      <AdminHeader onBackToPiloteClicked={onBackToPiloteClicked} />

      <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4 pl-16">
        <div className="flex min-h-12 items-start justify-between">
          <h1 className="text-xl text-gray-900 select-none">
            {intl.formatMessage({ id: 'promotions.placements.title' })}
          </h1>
          <SearchBox onChange={setSearchTerm} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <NavGroup>
            <NavGroupItem
              label={intl.formatMessage({ id: 'promotions.link.national' })}
              from="/promotions/$initiativeId/placements"
              to="/promotions/$initiativeId/national"
            />
            <NavGroupItem
              label={intl.formatMessage({ id: 'promotions.link.placements' })}
              from="/promotions/$initiativeId/national"
              to="/promotions/$initiativeId/placements"
            />
          </NavGroup>
          <Button size="sm" disabled={!onExport} onClick={onExport} loading={isExportPending}>
            <Download className="size-3.5" strokeWidth={2.6} aria-hidden="true" />
            {intl.formatMessage({ id: 'promotions.placements.export' })}
          </Button>
        </div>

        <div className="flex flex-col gap-4">
          <PromotionPlacementsForm regions={filteredRegions} {...props} />
          <p className="flex items-center gap-1.5 text-sm text-gray-500">
            <Lock className="size-3 shrink-0 text-gray-600" strokeWidth={2.6} aria-hidden="true" />
            {intl.formatMessage({ id: 'promotions.placements.lockedLegend' })}
          </p>
        </div>
      </div>
    </div>
  )
}
